import { Component, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SubscriptionStateService, SubscriptionState } from '../../../core/services/subscription-state.service';
import { PermissionsService } from '../../../core/services/permissions.service';
import { Auth } from '@angular/fire/auth';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-trial-banner',
  standalone: true,
  imports: [CommonModule, RouterModule],
  styles: [`
      .trial-banner-fixed {
        position: fixed;
        top: 70px;
        left: 250px;
        right: 0;
        z-index: 1000;
        background: #fff;

        body[data-sidebar-size="sm"] & {
          left: 70px;
        }
      }
    `],
  template: `
    <div class="trial-banner-fixed">

    <!-- Trial Banner -->
    <div class="alert alert-info border-0 mb-0 rounded-0 d-flex align-items-center justify-content-between"
         role="alert"
         *ngIf="state && state.isTrial && !state.isTrialCancelled && state.trialDaysRemaining !== null && state.trialDaysRemaining > 0">
      <div class="d-flex align-items-center">
        <i class="bx bx-gift font-size-18 me-2"></i>
        <span>
          <strong>Free Trial:</strong>
          {{ state.trialDaysRemaining }} day{{ state.trialDaysRemaining === 1 ? '' : 's' }} remaining.
          Subscribe now to keep all features.
        </span>
      </div>
      <a routerLink="/organization/subscription" 
         class="btn btn-sm btn-primary ms-3"
         *ngIf="canManageSubscription">
        Subscribe
      </a>
    </div>

    <!-- Trial Cancelled Banner -->
    <div class="alert alert-warning border-0 mb-0 rounded-0 d-flex align-items-center justify-content-between"
         role="alert"
         *ngIf="state && state.isTrial && state.isTrialCancelled">
      <div class="d-flex align-items-center">
        <i class="bx bx-x-circle font-size-18 me-2"></i>
        <span>
          <strong>Trial Cancelled:</strong>
          Your free trial has been cancelled.
          <ng-container *ngIf="state.trialEndDate">
            You have access until <strong>{{ state.trialEndDate | date:'d MMMM yyyy' }}</strong>.
          </ng-container>
          Resubscribe to keep your features.
        </span>
      </div>
      <a routerLink="/organization/subscription" 
         class="btn btn-sm btn-warning ms-3"
         *ngIf="canManageSubscription">
        Resubscribe
      </a>
    </div>

    <!-- Grace Period Banner -->
    <div class="alert alert-warning border-0 mb-0 rounded-0 d-flex align-items-center justify-content-between"
         role="alert"
         *ngIf="state && state.isGrace && state.graceDaysRemaining !== null">
      <div class="d-flex align-items-center">
        <i class="bx bx-error-circle font-size-18 me-2"></i>
        <span>
          <strong>Subscription Expired:</strong>
          {{ state.graceDaysRemaining }} day{{ state.graceDaysRemaining === 1 ? '' : 's' }} of access remaining.
          Renew now to avoid losing access.
        </span>
      </div>
      <a routerLink="/organization/subscription" 
         class="btn btn-sm btn-warning ms-3"
         *ngIf="canManageSubscription">
        Renew
      </a>
    </div>

    <!-- Payment Failed Banner -->
    <div class="alert alert-danger border-0 mb-0 rounded-0 d-flex align-items-center justify-content-between"
         role="alert"
         *ngIf="state && state.status === 'payment_failed'">
      <div class="d-flex align-items-center">
        <i class="bx bx-credit-card font-size-18 me-2"></i>
        <span>
          <strong>Payment Failed:</strong>
          Please update your payment method to continue using Echo.
        </span>
      </div>
      <a routerLink="/organization/subscription" 
         class="btn btn-sm btn-danger ms-3"
         *ngIf="canManageSubscription">
        Fix Payment
      </a>
    </div>

    <!-- Congregation Size Cap Banner -->
    <div class="alert alert-warning border-0 mb-0 rounded-0 d-flex align-items-center justify-content-between"
         role="alert"
         *ngIf="overCongregationCap">
      <div class="d-flex align-items-center">
        <i class="bx bx-group font-size-18 me-2"></i>
        <span>
          <strong>Congregation limit reached:</strong>
          You have {{ state?.congregationSize }} people, at or over your plan's limit of
          {{ state?.limits?.maxCongregationSize }}. CSV imports are paused until you're back
          under the limit or you upgrade.
        </span>
      </div>
      <a routerLink="/organization/subscription"
         class="btn btn-sm btn-warning ms-3"
         *ngIf="canManageSubscription">
        Upgrade Plan
      </a>
    </div>

    <!-- Setup Banner (onboarding not finished, so the trial hasn't started) -->
    <div class="alert alert-info border-0 mb-0 rounded-0 d-flex align-items-center justify-content-between"
         role="alert"
         *ngIf="state && state.isOnboarding">
      <div class="d-flex align-items-center">
        <i class="bx bx-rocket font-size-18 me-2"></i>
        <span>
          <strong>Finish Setting Up:</strong>
          Your 7-day free trial starts as soon as setup is complete.
        </span>
      </div>
    </div>

    <!-- Blocked Banner (no subscription) -->
    <div class="alert alert-danger border-0 mb-0 rounded-0 d-flex align-items-center justify-content-between"
         role="alert"
         *ngIf="state && state.isBlocked && !state.isOnboarding">
      <div class="d-flex align-items-center">
        <i class="bx bx-lock font-size-18 me-2"></i>
        <span>
          <strong>No Active Subscription:</strong>
          Subscribe to access all features.
        </span>
      </div>
      <a routerLink="/organization/subscription" 
         class="btn btn-sm btn-danger ms-3"
         *ngIf="canManageSubscription">
        Subscribe
      </a>
    </div>

    </div>
  `
})
export class TrialBannerComponent implements OnDestroy {
  private auth = inject(Auth);
  private permissionsService = inject(PermissionsService);
  private subscriptionStateService = inject(SubscriptionStateService);
  private sub: Subscription;

  state: SubscriptionState | null = null;
  canManageSubscription = false;

  get overCongregationCap(): boolean {
    const s = this.state;
    return !!s && s.congregationSize !== null && s.limits?.maxCongregationSize != null &&
      s.congregationSize >= s.limits.maxCongregationSize;
  }

  constructor() {
    this.sub = this.subscriptionStateService.state$.subscribe(state => {
      this.state = state;
    });

    // Load user permissions to check if they can manage subscriptions
    this.loadPermissions();
  }

  private async loadPermissions(): Promise<void> {
    const user = this.auth.currentUser;
    if (user) {
      const permissions = await this.permissionsService.getUserPermissions(user.uid);
      this.canManageSubscription = permissions.canManageSubscriptions;
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
